import React, { useState, useEffect, useContext, useCallback } from "react";

import PropTypes from "prop-types";

import { useLocation } from "react-router-dom";

import {
  Table,
  TableBody,
  TableContainer,
  Paper,
  Grid,
} from "@material-ui/core";

import useMediaQuery from "@material-ui/core/useMediaQuery";

import Loader from "../../UI/Loader";
import Pagination from "../../Pagination/Pagination";
import { useStyles } from "./style";
import { headCells } from "./HeadCells";

import { RecruitmentContext } from "../../../context/states/recruitmentState";
import { SearchContext } from "../../../context/states/search/searchState";

import MemoizedToolbar from "./memos/MemoizedToolbar";
import MemoizedHeader from "./memos/MemoizedHeader";
import MemoizedList from "./memos/MemoizedList";
import MemoizedAddNotes from "./memos/MemoizedAddNotes";

export const Recruitments = ({ title }) => {
  const classes = useStyles();
  const matches = useMediaQuery("(min-width:1920px)");
  const location = useLocation();

  const {
    getRecruitmentsHandler,
    getRecruitmentHandler,
    updateRecruitmentNotesHandler,
    data,
    loading,
    total,
    count,
    load,
    recruitment,
    recruitmentNotes,
  } = useContext(RecruitmentContext);

  const { recruitmentSearchHandler, recruitmentSearch } =
    useContext(SearchContext);

  // ---------------- SEARCH STATE ----------------
  const [isSearchObj, setIsSearchObj] = useState({
    recId: recruitmentSearch?.recId || "",
    name: recruitmentSearch?.name || "",
    email: recruitmentSearch?.email || "",
    contactNumber: recruitmentSearch?.contactNumber || "",
    language: recruitmentSearch?.language || "",
    dialect: recruitmentSearch?.dialect || "",
    gender: recruitmentSearch?.gender || "",
    nationality: recruitmentSearch?.nationality || "",
    statusInUk: recruitmentSearch?.statusInUk || "",

    hasDuplicates: recruitmentSearch?.hasDuplicates ?? false,

    page: recruitmentSearch?.page || 1,
    limit: recruitmentSearch?.limit || 50,
  });

  // ---------------- TABLE STATE ----------------
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [selected, setSelected] = useState([]);
  const [dense, setDense] = useState(false);

  const [isPage, setPage] = useState(isSearchObj.page);
  const [rowsPerPage, setRowsPerPage] = useState(isSearchObj.limit);

  const [isNotesOpen, setIsNotesOpen] = useState(false);

  // ---------------- CLEAR SEARCH ----------------
  const clearAdvancedSearchHandler = useCallback(() => {
    setIsSearchObj({
      recId: "",
      name: "",
      email: "",
      contactNumber: "",
      language: "",
      dialect: "",
      gender: "",
      nationality: "",
      statusInUk: "",

      // Reset duplicate filter
      hasDuplicates: false,

      page: 1,
      limit: 50,
    });

    setPage(1);
    setRowsPerPage(50);
  }, []);

  // ---------------- FETCH DATA ----------------
  useEffect(() => {
    const query = recruitmentSearchHandler(isSearchObj);

    getRecruitmentsHandler(query);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchObj]);

  // ---------------- SORT ----------------
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // ---------------- SELECT ----------------
  const handleClick = useCallback(
    (event, id) => {
      const selectedIndex = selected.indexOf(id);

      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else {
        newSelected = selected.filter((i) => i !== id);
      }

      setSelected(newSelected);
    },
    [selected],
  );

  const isSelected = useCallback(
    (id) => selected.indexOf(id) !== -1,
    [selected],
  );

  // ---------------- NOTES ----------------
  const getRecruitmentNotesHandler = useCallback(
    (id) => {
      getRecruitmentHandler(id).then(() => setIsNotesOpen(true));
    },
    [getRecruitmentHandler],
  );

  const closeNotesModal = useCallback(() => {
    setIsNotesOpen(false);
  }, []);

  const recruitmentNoteHandler = useCallback(
    (note) => {
      updateRecruitmentNotesHandler(note);
    },
    [updateRecruitmentNotesHandler],
  );

  // ---------------- PAGINATION ----------------
  const handleChangePage = useCallback(
    (event, newPage) => {
      const updatedPage = newPage + 1;

      setPage(updatedPage);

      setIsSearchObj((prev) => ({
        ...prev,
        page: updatedPage,
        limit: rowsPerPage,
      }));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = useCallback((event) => {
    const limit = parseInt(event.target.value, 10);

    setRowsPerPage(limit);
    setPage(1);

    setIsSearchObj((prev) => ({
      ...prev,
      page: 1,
      limit,
    }));
  }, []);

  // ---------------- UI ----------------
  return (
    <div className={classes.root}>
      <Loader open={load} handleClose={() => {}} />

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {/* Toolbar */}
          <MemoizedToolbar
            title={title}
            selected={selected}
            isCheckbox={false}
            openFormModalHandler={() => {}}
            refreshDataHandler={clearAdvancedSearchHandler}
            isSearchObj={isSearchObj}
            setIsSearchObj={setIsSearchObj}
            clearAdvancedSearchHandler={clearAdvancedSearchHandler}
            count={count}
          />

          {/* Table */}
          <TableContainer
            className={matches ? classes.container2 : classes.container}
          >
            <Table stickyHeader size={dense ? "small" : "medium"}>
              <MemoizedHeader
                headCells={headCells}
                selected={selected}
                order={order}
                orderBy={orderBy}
                handleRequestSort={handleRequestSort}
                data={data}
              />

              <TableBody>
                <MemoizedList
                  data={data}
                  loading={loading}
                  order={order}
                  orderBy={orderBy}
                  isPage={isPage}
                  rowsPerPage={rowsPerPage}
                  isSelected={isSelected}
                  handleClick={handleClick}
                  handleNoteRequest={getRecruitmentNotesHandler}
                />
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Pagination
            isTotal={total}
            isPage={isPage - 1}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />

          {/* Notes */}
          {isNotesOpen && (
            <MemoizedAddNotes
              isNotes={isNotesOpen}
              recruitment={recruitment}
              notesCloseHandler={closeNotesModal}
              onNoteSubmitHandler={recruitmentNoteHandler}
            />
          )}
        </Paper>
      </Grid>
    </div>
  );
};

Recruitments.propTypes = {
  title: PropTypes.string,
};
